import { IConfirmDialogData } from '../../../shared/interfaces';
import { TranslationService } from '../../shared/services/translation.service';
import { IUser } from '../users-management/interfaces';
import {
  ColorOptions,
  DESKTOP_PLATFORM,
  desktopPermissions,
  mobilePermissions,
} from './constants';
import { Actions, ClientPlatform } from './enum';
import { IAddRole, IPermission, IPermissionNode, IRole } from './interfaces';
import { Observable, combineLatest, map } from 'rxjs';

export function createTempRole(): IRole {
  const tempRole: IRole = {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    icon: {
      name: DESKTOP_PLATFORM,
      color: ColorOptions[3].color,
      background: ColorOptions[3].background,
    },
    assignedUsers: [],
    permissions: {
      desktop: nodesToPermission(desktopPermissions),
      mobile: nodesToPermission(mobilePermissions),
    },
    platformEnabled: ClientPlatform.DESKTOP,
    updatedAt: new Date(),
  };
  return structuredClone(tempRole);
}

export function getDeleteRoleDialogData(roleName: string): IConfirmDialogData {
  return {
    title: 'deleteRoleDialogTitle',
    content: 'deleteRoleDialogContent',
    contentParams: { roleName },
    cancelLabel: 'cancel',
    confirmLabel: 'confirm',
  };
}

export function searchUsers(
  users: Partial<IUser>[],
  query: string,
): Partial<IUser>[] {
  if (!query) return users;
  const normalizedQuery = query.toLowerCase();
  return users.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`;
    return (
      fullName.toLowerCase().includes(normalizedQuery) ||
      user.email?.toLowerCase().includes(normalizedQuery)
    );
  });
}

export function createRoleObj(form: IAddRole, role: IRole): IRole {
  return {
    ...role,
    name: form.name,
    description: form.description,
    assignedUsers: role.assignedUsers,
    icon: {
      name: role.icon.name,
      color: form.icon.color,
      background: form.icon.background,
    },
    permissions: role.permissions,
  };
}

export function createAssignedUsers(
  currentUsers: string[],
  newUsers: string[],
): string[] {
  if (
    currentUsers.length === newUsers.length &&
    currentUsers.every((id) => newUsers.includes(id))
  ) {
    return [];
  }
  return newUsers.filter((id) => !currentUsers.includes(id));
}

export function filterAssignedUsers(
  users: IUser[],
  assignedUsers: string[],
): IUser[] {
  return users.filter((user) => {
    return !assignedUsers.some((assignedUser) => assignedUser === user.id);
  });
}

export function nodesToPermission(
  nodes: IPermissionNode[],
  emptyActions?: boolean,
): IPermission[] {
  return nodes.map((node) => {
    const parentActions = node.actions ?? [];
    const childActions =
      node.children
        ?.flatMap((child) => child.actions ?? [])
        .filter((_, i, arr) => arr.indexOf(_) === i) ?? [];

    const allActions = !emptyActions
      ? Array.from(new Set([...parentActions, ...childActions]))
      : [];
    return {
      resource: node.resource,
      actions: allActions,
    };
  });
}

export function permissionToNodes(
  permissions: IPermission[],
  platformPermissions: IPermissionNode[],
): IPermissionNode[] {
  return platformPermissions.map((node) => {
    const entry = permissions.find((p) => p.resource === node.resource);
    const entryActions = entry?.actions ?? [];

    const children =
      node.children?.map((child) => {
        const hasAll = child.actions.every((a) => entryActions.includes(a));
        return {
          ...child,
          selected: hasAll,
          actions: child.actions,
        };
      }) || [];

    const parentHasAll = node.actions.every((a) => entryActions.includes(a));

    return {
      ...node,
      selected: parentHasAll,
      actions: entryActions,
      children,
    };
  });
}

export function getParentNode(
  node: IPermissionNode,
  allNodes: IPermissionNode[],
): IPermissionNode | undefined {
  return allNodes.find((n) => n.resource === node.resource && !!n.children);
}

function checkParentNode(updatedNode: IPermissionNode): Actions[] {
  const childActions = updatedNode.children!.flatMap((c) => c.actions) || [];
  const REQUIRED_BASE_ACTION = Actions.READ;
  return [REQUIRED_BASE_ACTION, ...new Set(childActions)];
}

function checkSingleNode(
  allNodes: IPermissionNode[],
  updatedNode: IPermissionNode,
): Actions[] {
  return allNodes.find((p) => p.resource === updatedNode.resource)
    ?.actions as Actions[];
}

export function checkChildNode(
  updatedNode: IPermissionNode,
  checked: boolean,
  currentPerms: IPermission[],
  allNodes: IPermissionNode[],
): Actions[] {
  const parentNode = getParentNode(updatedNode, allNodes);
  if (checked) {
    parentNode!.selected = true;
  }

  const currentActions = currentPerms.find(
    (p) => p.resource === updatedNode.resource,
  )?.actions;
  const childActions = updatedNode.actions;

  const newActions = Array.from(
    new Set([
      ...(currentActions || []),
      ...parentNode!.actions,
      ...childActions,
    ]),
  );

  return checked
    ? newActions
    : newActions.filter((action) => !childActions.includes(action));
}

export function togglePermissionNode(
  node: IPermissionNode,
  checked: boolean,
  currentPerms: IPermission[],
  allNodes: IPermissionNode[],
): IPermission[] {
  const updatedNode = { ...node, selected: checked };

  let actionsToApply: Actions[] = [];
  const isParentNode: boolean = (updatedNode.children?.length ?? 0) > 0;
  const isSingleNode: boolean = !!updatedNode.single;
  const isChildNode: boolean = !isParentNode && !isSingleNode;

  if (isSingleNode && checked) {
    actionsToApply = checkSingleNode(allNodes, updatedNode);
  }

  if (isParentNode && checked) {
    actionsToApply = checkParentNode(updatedNode);
  }

  if (isChildNode) {
    actionsToApply = checkChildNode(
      updatedNode,
      checked,
      currentPerms,
      allNodes,
    );
  }

  const updatedPermissions = currentPerms.map((perm) =>
    perm.resource === updatedNode.resource
      ? { resource: updatedNode.resource, actions: actionsToApply }
      : perm,
  );

  const resourceExists = currentPerms.some(
    (perm) => perm.resource === updatedNode.resource,
  );

  if (!resourceExists && actionsToApply.length > 0) {
    updatedPermissions.push({
      resource: updatedNode.resource,
      actions: actionsToApply,
    });
  }

  return updatedPermissions.filter((perm) => perm.actions.length > 0);
}

export function upsertRole(roles: IRole[], role: IRole): IRole[] {
  const index = roles.findIndex((r) => r.id === role.id);
  return index === -1
    ? [...roles, role]
    : roles.map((r) => (r.id === role.id ? role : r));
}

export function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
}

export function getTranslatedDesktopPermissions(translationService: TranslationService): Observable<IPermissionNode[]> {  
  return combineLatest([
    // Form Builder section
    translationService.getTranslation('formBuilderPermission'),
    translationService.getTranslation('formBuilderDescription'),
    translationService.getTranslation('createEditFormsPermission'),
    translationService.getTranslation('createEditFormsDescription'),
    translationService.getTranslation('deleteFormsPermission'),
    translationService.getTranslation('deleteFormsDescription'),
    // User Management section
    translationService.getTranslation('userManagementPermission'),
    translationService.getTranslation('userManagementDescription'),
    translationService.getTranslation('createUsersPermission'),
    translationService.getTranslation('createUsersDescription'),
    translationService.getTranslation('deleteUsersPermission'),
    translationService.getTranslation('deleteUsersDescription'),
    translationService.getTranslation('changeRolesPermission'),
    translationService.getTranslation('changeRolesDescription'),
    // Roles Management section
    translationService.getTranslation('rolesManagementPermission'),
    translationService.getTranslation('rolesManagementDescription'),
    translationService.getTranslation('createEditRolesPermission'),
    translationService.getTranslation('createEditRolesDescription'),
    translationService.getTranslation('deleteRolesPermission'),
    translationService.getTranslation('deleteRolesDescription'),
    // Reports section
    translationService.getTranslation('reportsPermission'),
    translationService.getTranslation('reportsDescription'),
  ]).pipe(
    map(translations => {      
      const permissions: IPermissionNode[] = [
        {
          resource: desktopPermissions[0].resource,
          name: translations[0] || 'Form Builder',
          actions: desktopPermissions[0].actions,
          description: translations[1] || 'Access and view the form templates list.',
          selected: true,
          children: [
            {
              name: translations[2] || 'Create and edit forms',
              actions: desktopPermissions[0].children![0].actions,
              selected: true,
              resource: desktopPermissions[0].children![0].resource,
              description: translations[3] || 'Create new Forms, edit existing ones and assign forms to Roles.',
            },
            {
              name: translations[4] || 'Delete forms',
              actions: desktopPermissions[0].children![1].actions,
              selected: true,
              resource: desktopPermissions[0].children![1].resource,
              description: translations[5] || 'Delete form templates created in the Forms tab.',
            },
          ],
        },
        {
          resource: desktopPermissions[1].resource,
          name: translations[6] || 'User Management',
          actions: desktopPermissions[1].actions,
          description: translations[7] || 'Access to Users page.',
          selected: true,
          children: [
            {
              name: translations[8] || 'Create users',
              actions: desktopPermissions[1].children![0].actions,
              description: translations[9] || 'Create new Users and assign roles.',
              selected: true,
              resource: desktopPermissions[1].children![0].resource,
            },
            {
              name: translations[10] || 'Delete Users',
              actions: desktopPermissions[1].children![1].actions,
              description: translations[11] || 'Delete any user from the Users page.',
              selected: true,
              resource: desktopPermissions[1].children![1].resource,
            },
            {
              name: translations[12] || 'Change roles',
              actions: desktopPermissions[1].children![2].actions,
              description: translations[13] || 'Change roles of Users.',
              selected: true,
              resource: desktopPermissions[1].children![2].resource,
            },
          ],
        },
        {
          resource: desktopPermissions[2].resource,
          name: translations[14] || 'Roles Management',
          actions: desktopPermissions[2].actions,
          description: translations[15] || 'Access to Roles page.',
          selected: true,
          children: [
            {
              name: translations[16] || 'Create and Edit Roles',
              actions: desktopPermissions[2].children![0].actions,
              description: translations[17] || 'Create new Roles and edit existing ones. In each Role you will be able to define its permissions and assign the Role to users.',
              selected: true,
              resource: desktopPermissions[2].children![0].resource,
            },
            {
              name: translations[18] || 'Delete Roles',
              actions: desktopPermissions[2].children![1].actions,
              description: translations[19] || 'Delete an empty Role. The Role can only be deleted once all assigned members have been removed.',
              selected: true,
              resource: desktopPermissions[2].children![1].resource,
            },
          ],
        },
        {
          resource: desktopPermissions[3].resource,
          name: translations[20] || 'Reports',
          actions: desktopPermissions[3].actions,
          description: translations[21] || 'Access to Reports page.',
          selected: true,
        },
      ];

      return permissions;
    })
  );
}

export function getTranslatedMobilePermissions(translationService: TranslationService): Observable<IPermissionNode[]> {
  return combineLatest([
    translationService.getTranslation('formsPermission'),
    translationService.getTranslation('formsDescription'),
  ]).pipe(
    map(translations => {
      console.log('Mobile translations received:', translations);
      
      const permissions: IPermissionNode[] = [
        {
          resource: mobilePermissions[0].resource,
          name: translations[0] || 'Forms',
          actions: mobilePermissions[0].actions,
          description: translations[1] || 'View and Fill forms in the mobile app. If this permission is disabled the Role will not have access to the mobile app.',
          selected: false,
          single: true,
        },
      ];

      return permissions;
    })
  );
}

export function getTranslatedDeleteRoleDialogData(roleName: string, translationService: TranslationService): Observable<IConfirmDialogData> {
  return combineLatest([
    translationService.getTranslation('deleteRoleDialogTitle'),
    translationService.getTranslation('deleteRoleDialogContent', { roleName }),
    translationService.getTranslation('cancel'),
    translationService.getTranslation('confirm'),
  ]).pipe(
    map(translations => {
      console.log('Dialog translations received:', translations);
      
      const dialogData: IConfirmDialogData = {
        title: translations[0] || 'Delete Role',
        content: translations[1] || `"${roleName}" will be deleted permanently`,
        cancelLabel: translations[2] || 'Cancel',
        confirmLabel: translations[3] || 'Confirm',
      };

      return dialogData;
    })
  );
}

export function getTranslatedErrorDialogData(translationService: TranslationService): Observable<{
  deleteRoleError: {
    icon: string;
    title: string;
    message: string;
    action: string;
  };
  roleNameExists: {
    icon: string;
    title: string;
    message: string;
  };
  permissionsChanged: {
    icon: string;
    title: string;
    message: string;
  };
}> {
  return combineLatest([
    translationService.getTranslation('unableDeleteRoleTitle'),
    translationService.getTranslation('unableDeleteRoleMessage'),
    translationService.getTranslation('cancel'),
    translationService.getTranslation('roleNameInUseTitle'),
    translationService.getTranslation('roleNameInUseMessage'),
    translationService.getTranslation('roleEmptyTitle'),
    translationService.getTranslation('roleEmptyMessage'),
  ]).pipe(
    map(translations => {      
      const dialogData = {
        deleteRoleError: {
          icon: 'error_outline',
          title: translations[0] || 'Unable to delete Role',
          message: translations[1] || 'A role cannot be deleted while people are assigned to it. Please reassign the users to another role first.',
          action: translations[2] || 'Cancel',
        },
        roleNameExists: {
          icon: 'error_outline',
          title: translations[3] || 'Role name in use',
          message: translations[4] || 'This name is already being used for another permission, please choose a different name.',
        },
        permissionsChanged: {
          icon: 'error_outline',
          title: translations[5] || 'Role is empty',
          message: translations[6] || 'Please choose at least one permission to assign to this role.',
        },
      };

      return dialogData;
    })
  );
}

export function getTranslatedSnackbarMessages(translationService: TranslationService): Observable<{
  create: string;
  delete: string;
}> {
  return combineLatest([
    translationService.getTranslation('roleCreated'),
    translationService.getTranslation('roleDeleted'),
  ]).pipe(
    map(translations => {      
      const messages = {
        create: translations[0] || 'Role created',
        delete: translations[1] || 'Role deleted',
      };

      return messages;
    })
  );
}

export function createTempRoleWithTranslations(translationService: TranslationService): Observable<IRole> {
  return combineLatest([
    getTranslatedDesktopPermissions(translationService),
    getTranslatedMobilePermissions(translationService),
  ]).pipe(
    map(([desktopPerms, mobilePerms]) => {
        const tempRole: IRole = {
        id: crypto.randomUUID(),
        name: '',
        description: '',
        icon: {
          name: DESKTOP_PLATFORM,
          color: ColorOptions[3].color,
          background: ColorOptions[3].background,
        },
        assignedUsers: [],
        permissions: {
          desktop: nodesToPermission(desktopPerms || []),
          mobile: nodesToPermission(mobilePerms || []),
        },
        platformEnabled: ClientPlatform.DESKTOP,
        updatedAt: new Date(),
      };
      
      return structuredClone(tempRole);
    })
  );
}

export function translatePermissionNodes(
  nodes: IPermissionNode[],
  translationService: TranslationService
): Observable<IPermissionNode[]> {
  return new Observable(subscriber => {
    if (nodes === desktopPermissions) {
      getTranslatedDesktopPermissions(translationService).subscribe({
        next: (translatedNodes) => {
          subscriber.next(translatedNodes);
          subscriber.complete();
        },
        error: (error) => subscriber.error(error)
      });
    }
    else if (nodes === mobilePermissions) {
      getTranslatedMobilePermissions(translationService).subscribe({
        next: (translatedNodes) => {
          subscriber.next(translatedNodes);
          subscriber.complete();
        },
        error: (error) => subscriber.error(error)
      });
    }
    else {
      subscriber.next(nodes);
      subscriber.complete();
    }
  });
}

